/**
 * SFTP authentication mode — aligns with Workflow Service / OpenAPI naming (PascalCase enum values).
 * Legacy workflows omit AuthType; UI treats that as password (FR25).
 */
import { isSftpSshKeyAuthFeatureEnabled } from "../utils/sftp-ssh-key-feature-gate";

export const SFTP_AUTH_TYPE = {
  Password: "Password",
  SshKey: "SshKey",
} as const;

export type SftpAuthType = (typeof SFTP_AUTH_TYPE)[keyof typeof SFTP_AUTH_TYPE];

export const SFTP_AUTH_TYPE_FORM_OPTIONS = [
  { value: SFTP_AUTH_TYPE.Password, name: "Password" },
  { value: SFTP_AUTH_TYPE.SshKey, name: "SSH private key" },
];

/** Auth mode after applying LD `jhft_show_sftp_ssh_option` / optional `VITE_ENABLE_SFTP_SSH_KEY_AUTH` (when off, SshKey → Password). */
export function effectiveSftpAuthType(v: string | undefined | null): SftpAuthType {
  const n = normalizeSftpAuthType(v);
  if (!isSftpSshKeyAuthFeatureEnabled() && n === SFTP_AUTH_TYPE.SshKey) {
    return SFTP_AUTH_TYPE.Password;
  }
  return n;
}

/** Options for SFTP/FTPS auth radio or select; omits SSH key when LD / Vite override disables the feature. */
export function getSftpAuthTypeFormOptions(): typeof SFTP_AUTH_TYPE_FORM_OPTIONS {
  if (!isSftpSshKeyAuthFeatureEnabled()) {
    return SFTP_AUTH_TYPE_FORM_OPTIONS.filter((o) => o.value !== SFTP_AUTH_TYPE.SshKey);
  }
  return SFTP_AUTH_TYPE_FORM_OPTIONS;
}

export function normalizeSftpAuthType(v: string | undefined | null): SftpAuthType {
  if (v === undefined || v === null || String(v).trim() === "") {
    return SFTP_AUTH_TYPE.Password;
  }
  const s = String(v).trim();
  const lower = s.toLowerCase();
  if (s === SFTP_AUTH_TYPE.SshKey || lower === "sshkey" || lower === "ssh") {
    return SFTP_AUTH_TYPE.SshKey;
  }
  if (lower === "password") {
    return SFTP_AUTH_TYPE.Password;
  }
  return SFTP_AUTH_TYPE.Password;
}

export function isSftpSshKeyAuthType(v: string | undefined | null): boolean {
  return effectiveSftpAuthType(v) === SFTP_AUTH_TYPE.SshKey;
}
